import { useStripe } from "@stripe/react-stripe-js";
import React, { useState } from "react";
import { toast } from "react-toastify";
import {
  createStripeSubscription,
  getStripeSubscriptionHistory,
} from "../api/apiFunctions";

function SubscriptionPaymentSection({ selectedPackageId, onCompleted }) {
  const stripe = useStripe();
  const [loading, setLoading] = useState(false);

  const handleCreateSubscription = async () => {
    if (!selectedPackageId) {
      toast.error("Please select a subscription package first.");
      return;
    }

    const token =
      localStorage.getItem("authToken") || sessionStorage.getItem("authToken");

    if (!token) {
      toast.error("Authentication token not found. Please login again.");
      return;
    }

    setLoading(true);

    try {
      const response = await createStripeSubscription(
        { subscriptionPackageId: selectedPackageId },
        token,
      );

      // Non‑3DS payment
      if (response.success && !response.requires_action) {
        // Double check subscription status
        try {
          const history = await getStripeSubscriptionHistory(token);
          const activeSub = history.subscriptions?.find(
            (sub) => sub.status === "active" && sub.isActive,
          );

          if (!activeSub) {
            throw new Error(
              "Subscription was created but not marked active. Please contact support.",
            );
          }
        } catch (e) {
          console.error("Subscription history check failed:", e);
        }

        toast.success("Subscription activated successfully.");
        if (onCompleted) {
          onCompleted({
            type: "non_3ds",
            subscriptionId: response.subscription_id,
          });
        }
      }
      // 3DS required for payment
      else if (response.requires_action && response.client_secret) {
        if (!stripe) {
          throw new Error("Stripe is not initialized yet.");
        }

        const result = await stripe.confirmCardPayment(response.client_secret);

        if (result.error) {
          throw new Error(result.error.message);
        }

        if (result.paymentIntent.status !== "succeeded") {
          throw new Error(
            `Payment verification failed with status: ${result.paymentIntent.status}`,
          );
        }

        try {
          const history = await getStripeSubscriptionHistory(token);
          const activeSub = history.subscriptions?.find(
            (sub) => sub.status === "active" && sub.isActive,
          );

          if (!activeSub) {
            throw new Error(
              "Payment succeeded but subscription is not active yet. Please refresh or contact support.",
            );
          }
        } catch (e) {
          console.error("Subscription history check failed:", e);
        }

        toast.success("Subscription activated successfully.");
        if (onCompleted) {
          onCompleted({ type: "3ds" });
        }
      } else {
        throw new Error(response.message || "Failed to create subscription.");
      }
    } catch (err) {
      console.error("Create subscription error:", err);
      toast.error(
        err.message || "Failed to create subscription. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-2xl p-8 w-full max-w-xl border border-[#e5e7eb]">
        <h2 className="md:text-2xl text-xl font-extrabold text-[#2d348f] mb-4 text-center">
          Confirm Subscription & Pay
        </h2>
        <p className="text-sm text-[#837f90] mb-6 text-center">
          Your card is saved. Confirm your subscription to start using the app.
        </p>

        <button
          type="button"
          onClick={handleCreateSubscription}
          disabled={loading}
          className="w-full py-4 font-bold text-white text-base bg-gradient-to-r from-[#445eed] to-[#2d348f] hover:from-[#3916ab] hover:to-[#4962d8] transition-all rounded-xl shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-[1.02] active:scale-[0.98]"
        >
          {loading ? "Processing payment..." : "Start Subscription"}
        </button>
      </div>
    </div>
  );
}

export default SubscriptionPaymentSection;
